/**
 * VARSLINGSVALGENE (F5 og F51, runde 4. oktober).
 *
 * Atle: «Legg til valg i innstillinger på varslinger. Sikkert flere ting som kan ligge her.»
 *
 * Hver bruker slår av varseltyper hen ikke vil ha, én og én eller en hel gruppe. Valget lagres per
 * MENNESKE (`notification_opt_outs`, 0700) og gjelder i alle organisasjonene hen er med i. Basen
 * holder et avslått varsel ute før det skrives, så klokka, innboksen og oversikten er enige.
 *
 * GRUPPENE ER KODENS, ikke basens: en ny varseltype legges i en gruppe her (eller i
 * `MANDATORY_NOTIFICATION_TYPES`), og `notification-groups.test.ts` i weben holder hvert ikon i
 * `notification-icon.ts` ansvarlig for å stå ett av stedene.
 *
 * VARSLENE INGEN KAN SLÅ AV er medlemskapet (deaktivert, gjenopprettet, ny rolle — F62), frysingen
 * (F59), virusskanningen og eksporten man selv bestilte. Basen har den samme lista som en CHECK.
 */
import { z } from 'zod';

export const NOTIFICATION_GROUPS = [
  { id: 'messages', types: ['message.posted'] },
  {
    id: 'tasks',
    types: [
      'task.assigned',
      'task.co_assigned',
      'task.commented',
      'task.mentioned',
      'task.status_changed',
      'task.completed',
      'task.rescheduled',
      'task.due_soon',
      'task.overdue',
      'task.given',
      'task.given_completed',
    ],
  },
  { id: 'calendar', types: ['event.invited', 'event.responded', 'event.changed'] },
  {
    id: 'shifts',
    types: [
      'shift.assigned',
      'shift.changed',
      /* QA16 297.3–297.4. */
      'arrangement.crew_changed',
      'shift_request.opened',
      'shift_request.accepted',
      'shift_request.decided',
    ],
  },
  {
    id: 'booking',
    types: [
      'booking_enquiry.received',
      'booking_enquiry.answered',
      'booking_bid.withdrawn',
      /* 245.5: arrangøren godtok eller avslo budet. */
      'booking_bid.accepted',
      'booking_bid.declined',
      /* 253.80: budet ble godtatt, men årets bekreftede pris står. */
      'booking_bid.price_kept',
      'booking_agreement.changed',
      /* A8: den godtatte avtalen endret før signering. */
      'deal_sheet.amended',
      'booking_enquiry.party_shared',
      'booking_enquiry.party_changed',
      'booking_party.member_added',
      'shared_advance.opened',
      'contract_proposal.sent',
      'contract_proposal.decided',
      'concert.region_conflict',
    ],
  },
  {
    id: 'venue',
    types: [
      'venue_enquiry.received',
      'venue_enquiry.withdrawn',
      'venue_enquiry.concert_moved',
      'venue_enquiry.message_posted',
      'venue_room.spec_changed',
      /* QA15-S B: arrangementet laget av et signert bud, leid ut, avlyst og gjenopprettet. */
      'venue_night.auto_created',
      'venue_night.leased',
      'arrangement.cancelled',
      'arrangement.reinstated',
      'arrangement.moved',
      'date_request.held',
      'date_request.hold_extended',
      'date_request.promoted',
      'date_request.expired',
      'date_request.confirmed',
      'date_request.declined',
      'date_request.released',
      'date_request.superseded',
      'date_request.night_moved',
      'date_request.night_cancelled',
      'date_request.message_posted',
    ],
  },
  {
    id: 'sharing',
    types: [
      'share.created',
      'share.updated',
      'share.revoked',
      'share.expired',
      /* 284.2 (1145): manageren mistet tilgangen; de hen la til, er nå byråets. */
      'artist_access.giver_lost',
    ],
  },
  /* F5: en ekstern har åpnet delingen første gang, eller trykket «Bekreft mottatt». */
  { id: 'shareReceipts', types: ['share.opened', 'share.confirmed'] },
  {
    id: 'partners',
    types: [
      'organization_partner.requested',
      'organization_partner.accepted',
      'organization_partner.declined',
    ],
  },
  /* En person vil koble seg til deg (253.16) — personlig, ikke organisasjonens samarbeid. */
  { id: 'contacts', types: ['person_connection.requested'] },
] as const;

export type NotificationGroupId = (typeof NOTIFICATION_GROUPS)[number]['id'];

/** Kan ikke slås av. Speiler `ck_notification_opt_outs__not_mandatory` (0700). */
export const MANDATORY_NOTIFICATION_TYPES = [
  'organization.role_changed',
  'organization.member_deactivated',
  'organization.member_reactivated',
  'organization.frozen',
  'organization.unfrozen',
  /* 245.7: fjernet fra organisasjonen. */
  'organization.member_removed',
  'attachment.infected',
  /* docs/EKSPORT.md §8: brukerens egen bestilling — klar, eller ikke laget. */
  'export.ready',
  'export.failed',
] as const;

const ADJUSTABLE: ReadonlySet<string> = new Set(
  NOTIFICATION_GROUPS.flatMap((group) => [...group.types]),
);

export const ADJUSTABLE_NOTIFICATION_TYPES: readonly string[] = [...ADJUSTABLE];

/**
 * EN NY TYPE FINNER GRUPPEN SIN SELV, PÅ PREFIKSET (orkestratoren, pakke 3). En agent som legger til
 * `booking_counter.accepted`, trenger ikke å kjenne denne fila: `booking_*` er Booking. Lista over
 * er fortsatt der de kjente typene står i rekkefølge; prefiksene er for dem som kommer.
 */
const PREFIX_GROUPS: readonly (readonly [prefix: string, group: NotificationGroupId])[] = [
  ['message.', 'messages'],
  ['task.', 'tasks'],
  ['event.', 'calendar'],
  ['shift.', 'shifts'],
  ['booking_', 'booking'],
  ['contract_proposal.', 'booking'],
  ['shared_advance.', 'booking'],
  ['concert.', 'booking'],
  ['venue_', 'venue'],
  ['date_request.', 'venue'],
  ['organization_partner.', 'partners'],
  ['person_connection.', 'contacts'],
];

const TYPE_GROUP: ReadonlyMap<string, NotificationGroupId> = new Map(
  NOTIFICATION_GROUPS.flatMap((group) => group.types.map((type) => [type, group.id] as const)),
);

const MANDATORY: ReadonlySet<string> = new Set(MANDATORY_NOTIFICATION_TYPES);

/** Varsler om medlemskapet og frysingen (`organization.*`) og virusfunn kan aldri slås av. */
export const isMandatoryNotificationType = (type: string): boolean =>
  MANDATORY.has(type) || type.startsWith('organization.');

/** Gruppen en type hører til, eller null for en obligatorisk eller ukjent type. */
export function notificationGroupOf(type: string): NotificationGroupId | null {
  if (isMandatoryNotificationType(type)) return null;
  const known = TYPE_GROUP.get(type);
  if (known !== undefined) return known;
  for (const [prefix, group] of PREFIX_GROUPS) {
    if (type.startsWith(prefix)) return group;
  }
  return null;
}

/** Kan slås av: en type med en gruppe — kjent, eller funnet på prefikset. */
export const isAdjustableNotificationType = (type: string): boolean =>
  ADJUSTABLE.has(type) || notificationGroupOf(type) !== null;

/** Typene i en gruppe: de kjente, og de `known` (f.eks. ikonlista i weben) som prefikset legger der. */
export function notificationTypesIn(
  group: NotificationGroupId,
  known: readonly string[] = [],
): string[] {
  const listed = NOTIFICATION_GROUPS.find((one) => one.id === group)?.types ?? [];
  const extra = known.filter(
    (type) => !TYPE_GROUP.has(type) && notificationGroupOf(type) === group,
  );
  return [...listed, ...extra];
}

export const notificationPreferencesSchema = z
  .object({
    /** Typene personen har slått av. Alt annet er på. */
    off: z.array(z.string()),
  })
  .meta({ id: 'NotificationPreferences' });

export const putNotificationPreferencesRequestSchema = z
  .object({
    /** Hele lista i ett kall; en type som ikke står her, er på. */
    off: z
      .array(
        z.string().refine(isAdjustableNotificationType, {
          message: 'Not a notification type that can be switched off.',
        }),
      )
      .max(ADJUSTABLE_NOTIFICATION_TYPES.length),
  })
  .meta({ id: 'PutNotificationPreferencesRequest' });

export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;
export type PutNotificationPreferencesRequest = z.infer<
  typeof putNotificationPreferencesRequestSchema
>;
